import React from "react";
import {
  animations,
  styles,
  Hero,
  SectionHeader,
  CTASection,
} from "../../../components/landing/SharedComponent";
import { motion } from "framer-motion";

const About = () => {
  const values = [
    {
      title: "Quality",
      description:
        "Maintaining highest standards in cathodic protection and energy storage solutions",
      icon: "⭐",
    },
    {
      title: "Innovation",
      description:
        "Developing cutting-edge solutions for corrosion prevention and energy saving",
      icon: "🔬",
    },
    {
      title: "Sustainability",
      description:
        "Promoting environmentally friendly energy conservation technologies",
      icon: "🌱",
    },
  ];

  const achievements = [
    {
      number: "25+",
      title: "Projects Completed",
      description: "Successfully delivered projects across Indonesia",
    },
    {
      number: "15+",
      title: "Expert Team Members",
      description: "Skilled professionals dedicated to excellence",
    },
    {
      number: "5+",
      title: "Years Experience",
      description: "Serving industries since 2018 with expertise",
    },
    {
      number: "100%",
      title: "Client Satisfaction",
      description: "Committed to exceeding client expectations",
    },
  ];

  const journeyMilestones = [
    {
      year: 2018,
      title: "Company Foundation",
      description: "Established as PT. Adhim Aju Sejahtera Utama",
      icon: "🏢",
      achievement: "Received legal business registration",
    },
    {
      year: 2019,
      title: "First Major Projects",
      description: "Contracts with PT Freeport Indonesia and Jaskata Bersama",
      icon: "📈",
      achievement: "Cathodic protection supply & installation",
    },
    {
      year: 2020,
      title: "Project Expansion",
      description: "Expanded to power plant infrastructure",
      icon: "💡",
      achievement: "PLTU Sumbawa Barat project completion",
    },
    {
      year: 2022,
      title: "National Recognition",
      description:
        "Partnerships with major companies including Pertamina and PGN",
      icon: "🌏",
      achievement: "Completed major pipeline inspection projects",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero
        title="About Us"
        subtitle="Leading provider of cathodic protection and energy storage solutions since 2018"
        backgroundImage="/images/landing/bg.png"
      />

      {/* Company Overview Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`${styles.container} ${styles.sectionSpacing}`}
      >
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className={styles.elementSpacing}>
            <h2 className="font-barlow-semibold text-5xl lg:text-6xl text-primary mb-8">
              Our Vision & Mission
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              PT. Adhim Aju Sejahtera Utama was established in 2018 as a company
              specializing in cathodic protection for corrosion control and
              energy saving solutions to prevent heat loss.
            </p>
            <div className="space-y-6">
              {values.map((value, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-4 ${styles.transitionBase}`}
                >
                  <div className="text-5xl">{value.icon}</div>
                  <div>
                    <h3 className="text-xl font-semibold text-primary">
                      {value.title}
                    </h3>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className={`${styles.shadowLarge} bg-white rounded-lg overflow-hidden`}
          >
            <img
              src="/images/landing/about.png"
              alt="Company facility"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </motion.div>

      {/* Achievements Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className="bg-primary py-16 md:py-20"
      >
        <div className={styles.container}>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-4xl font-bold mb-4">
                  {achievement.number}
                </div>
                <h3 className="text-xl font-semibold mb-4">
                  {achievement.title}
                </h3>
                <p className="text-white/80">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Journey Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`bg-white ${styles.sectionSpacing}`}
      >
        <div className={styles.container}>
          <SectionHeader title="Our Journey" />
          <div className="grid md:grid-cols-4 gap-8">
            {journeyMilestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                variants={animations.fadeInUp}
                className="relative group"
              >
                {index < journeyMilestones.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary to-primary/20" />
                )}
                <div
                  className={`
                  relative z-10 w-24 h-24 mx-auto mb-6 rounded-full 
                  bg-primary text-white flex items-center justify-center 
                  text-2xl font-bold group-hover:scale-110 ${styles.transitionBase}
                `}
                >
                  {milestone.year}
                </div>
                <div
                  className={`
                  bg-gray-50 rounded-lg p-6 text-center 
                  group-hover:shadow-lg ${styles.transitionBase}
                `}
                >
                  <div className="text-4xl mb-4">{milestone.icon}</div>
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {milestone.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{milestone.description}</p>
                  <div className="text-sm text-primary/80 font-medium">
                    {milestone.achievement}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <CTASection
        title="Join Our Journey"
        subtitle="Partner with us to implement effective cathodic protection and energy saving solutions"
        buttonText="Contact Us"
        buttonLink="/contact"
      />
    </div>
  );
};

export default About;
